const ZONE = 'Europe/Belgrade';
// ICU's en-GB says "Sept"; the console's style is three letters.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "10:00:00" → "10:00". Null stays null: a missing start time is shown as TBA, never midnight. */
export function hhmm(time: string | null | undefined): string | null {
  if (!time) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(time);
  return match ? `${match[1]!.padStart(2, '0')}:${match[2]}` : null;
}

function belgradeParts(date: Date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return {
    year: get('year'),
    month: MONTHS[Number(get('month')) - 1] ?? '',
    day: String(Number(get('day'))),
    hour: get('hour'),
    minute: get('minute'),
  };
}

/** An API timestamp as an instant. The API sends UTC without a zone, which `Date` would read as local. */
export function toInstant(iso: string): Date {
  return new Date(/(Z|[+-]\d{2}:?\d{2})$/i.test(iso) || !iso.includes('T') ? iso : `${iso}Z`);
}

/** "24 Sep, 14:02" in Belgrade time, with the year when it isn't this year. */
export function formatTimestamp(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return '';
  const date = toInstant(iso);
  if (Number.isNaN(date.getTime())) return '';
  const p = belgradeParts(date);
  const year = p.year === belgradeParts(now).year ? '' : ` ${p.year}`;
  return `${p.day} ${p.month}${year}, ${p.hour}:${p.minute}`;
}

/** "24 Sep" in Belgrade, with the year when it isn't this year: the day of a timestamp. */
export function formatDay(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return '';
  const date = toInstant(iso);
  if (Number.isNaN(date.getTime())) return '';
  const p = belgradeParts(date);
  const year = p.year === belgradeParts(now).year ? '' : ` ${p.year}`;
  return `${p.day} ${p.month}${year}`;
}

/** "35 min", "2 h", "3 d": how long ago, for lists. */
export function formatAge(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return '';
  const minutes = Math.max(0, Math.floor((now.getTime() - toInstant(iso).getTime()) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

/** "45 s", "6 min 12 s", "1 h 3 min": how long something took. */
export function formatDuration(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return seconds % 60 ? `${minutes} min ${seconds % 60} s` : `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
}

const number = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

/** English number format for values the console owns: "21.1", "1,412". */
export const formatNumber = (value: number): string => number.format(value);

/** A file's size: "812 bytes", "229 KB", "10.5 MB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${formatNumber(bytes)} bytes`;
  if (bytes < 1024 * 1024) return `${formatNumber(Math.round(bytes / 1024))} KB`;
  return `${formatNumber(Math.round((bytes / 1024 / 1024) * 10) / 10)} MB`;
}

/** AI cost per run to four decimals, totals to two: "$0.0287", "$1.84". */
export function formatMoney(value: number, decimals: 2 | 4 = 2): string {
  return `$${value.toFixed(decimals)}`;
}

export const exactCents = (n: number): boolean => Math.abs(n * 100 - Math.round(n * 100)) < 1e-9;

/** A spend figure: whole cents, or four places while it is a fraction of a cent under a dollar. */
export const formatSpend = (n: number): string =>
  formatMoney(n, n > 0 && n < 1 && !exactCents(n) ? 4 : 2);

/** Today's date in Belgrade as YYYY-MM-DD. */
export function belgradeToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: ZONE }).format(now);
}

const utcDay = (date: string) => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
};

/** The YYYY-MM-DD date a number of calendar days after another (negative: before). */
export const addDays = (date: string, days: number): string =>
  new Date(utcDay(date) + days * 86_400_000).toISOString().slice(0, 10);

/** The calendar day before a YYYY-MM-DD date. */
export const dayBefore = (date: string): string => addDays(date, -1);

/** "20 October", with the year when it isn't in today's year. */
export const dayInWords = (date: string, today: string): string =>
  new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: date.slice(0, 4) === today.slice(0, 4) ? undefined : 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));

/** Whole days from one YYYY-MM-DD date to another: 0 on the day, negative once it's passed. */
export const daysBetween = (from: string, to: string): number =>
  Math.round((utcDay(to) - utcDay(from)) / 86_400_000);

/** Inclusive dates, YYYY-MM-DD; an open end is undefined. */
export interface DateWindow {
  start?: string;
  end?: string;
}

const later = (a: string | undefined, b: string | undefined) => (!a ? b : !b ? a : a > b ? a : b);
const earlier = (a: string | undefined, b: string | undefined) => (!a ? b : !b ? a : a < b ? a : b);

/** A window narrowed by a from–to filter, or null when nothing can match. */
export function narrowWindow(
  own: DateWindow,
  from: string | undefined,
  to: string | undefined,
): DateWindow | null {
  const start = later(own.start, from);
  const end = earlier(own.end, to);
  return start && end && start > end ? null : { start, end };
}

/** Whole years from a birth date (a date or the API's date-time) to a YYYY-MM-DD day; null if unknown. */
export function ageOn(birth: string | null | undefined, day: string): number | null {
  const born = /^(\d{4})-(\d{2})-(\d{2})/.exec(birth ?? '');
  const on = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!born || !on) return null;
  const [, by, bm, bd] = born.map(Number) as [number, number, number, number];
  const [, y, m, d] = on.map(Number) as [number, number, number, number];
  const age = y - by - (m < bm || (m === bm && d < bd) ? 1 : 0);
  return age >= 0 ? age : null;
}
